<template>
  <div class="lessons-header">
    <div class="header-left">
      <div class="title-section">
        <h2 class="lessons-title">Course Lessons</h2>
        <button @click="$emit('refresh')" class="refresh-button" title="Refresh lessons">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="refresh-icon">
            <path d="M23 4v6h-6M1 20v-6h6"/>
            <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
          </svg>
        </button>
      </div>
      <div class="lessons-stats">
        <div class="stat-item">
          <span class="stat-value">{{ totalLessons }}</span>
          <span class="stat-label">Total Lessons</span>
        </div>
        <div class="stat-item">
          <span class="stat-value">{{ formattedDuration }}</span>
          <span class="stat-label">Total Duration</span>
        </div>
        <div class="stat-item" v-if="lastUpdated">
          <span class="stat-value">{{ lastUpdated }}</span>
          <span class="stat-label">Last Updated</span>
        </div>
      </div>
    </div>

    <div class="header-right">
      <div class="controls-group">
        <div class="bulk-actions" v-if="selectedCount > 0">
          <button class="bulk-actions-button">
            Bulk Actions ({{ selectedCount }})
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="dropdown-icon">
              <path d="M6 9l6 6 6-6"/>
            </svg>
          </button>
          <div class="bulk-actions-dropdown">
            <button @click="$emit('bulk-move')">Move to Course</button>
            <button @click="$emit('bulk-status')">Update Status</button>
            <button @click="$emit('bulk-export')">Export Selected</button>
            <button @click="$emit('bulk-delete')" class="delete">Delete Selected</button>
          </div>
        </div>
        
        <button @click="$emit('toggle-geo-restrictions')" class="geo-button" :class="{ active: geoRestrictionsActive }">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="geo-icon">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
            <circle cx="12" cy="10" r="3"/>
          </svg>
          Geo Restrictions
        </button>
      </div>

      <div class="view-toggle-buttons">
        <button 
          @click="$emit('view-change', 'grid')" 
          class="view-toggle" 
          :class="{ active: currentView === 'grid' }"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="view-icon">
            <rect x="3" y="3" width="7" height="7" />
            <rect x="14" y="3" width="7" height="7" />
            <rect x="3" y="14" width="7" height="7" />
            <rect x="14" y="14" width="7" height="7" />
          </svg>
          Grid
        </button>
        <button 
          @click="$emit('view-change', 'kanban')" 
          class="view-toggle" 
          :class="{ active: currentView === 'kanban' }"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="view-icon">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <line x1="9" y1="3" x2="9" y2="21" />
            <line x1="15" y1="3" x2="15" y2="21" />
          </svg>
          Kanban
        </button>
        <button 
          @click="$emit('view-change', 'table')" 
          class="view-toggle" 
          :class="{ active: currentView === 'table' }"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="view-icon">
            <path d="M3 10h18M3 14h18M3 18h18M3 6h18"/>
          </svg>
          Table
        </button>
      </div>

      <button @click="$emit('create-lesson')" class="create-button">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="create-icon">
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
        Create New Lesson
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LessonsHeader',
  props: {
    totalLessons: {
      type: Number,
      required: true
    },
    totalDuration: {
      type: Number,
      required: true
    },
    currentView: {
      type: String,
      required: true
    },
    selectedCount: {
      type: Number,
      default: 0
    },
    filtersVisible: {
      type: Boolean,
      default: false
    },
    lastUpdated: {
      type: String,
      default: null
    },
    geoRestrictionsActive: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      sortBy: 'title'
    }
  },
  computed: {
    formattedDuration() {
      const minutes = Math.round(this.totalDuration / 60);
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return `${hours}h ${remainingMinutes}m`;
    }
  }
}
</script>

<style scoped>
.lessons-header {
  margin-bottom: 2.5rem;
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.title-section {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.lessons-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
  letter-spacing: -0.025em;
}

.lessons-stats {
  display: flex;
  gap: 2rem;
  padding: 1rem 0;
}

.stat-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a202c;
}

.stat-label {
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.controls-group {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.sort-dropdown {
  position: relative;
}

.sort-select {
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9375rem;
  background: #f8fafc;
  color: #1e293b;
  cursor: pointer;
  transition: all 0.2s ease;
}

.sort-select:hover {
  border-color: #cbd5e1;
}

.sort-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.filter-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9375rem;
  background: #f8fafc;
  color: #1e293b;
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-toggle.active {
  background: #2563eb;
  color: white;
  border-color: #2563eb;
}

.filter-icon {
  width: 18px;
  height: 18px;
}

.bulk-actions {
  position: relative;
}

.bulk-actions-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9375rem;
  background: #f8fafc;
  color: #1e293b;
  cursor: pointer;
  transition: all 0.2s ease;
}

.bulk-actions-button:hover {
  border-color: #cbd5e1;
}

.dropdown-icon {
  width: 16px;
  height: 16px;
}

.bulk-actions-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  min-width: 200px;
  display: none;
}

.bulk-actions:hover .bulk-actions-dropdown {
  display: block;
}

.bulk-actions-dropdown button {
  display: block;
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: none;
  text-align: left;
  font-size: 0.9375rem;
  color: #1e293b;
  cursor: pointer;
  transition: all 0.2s ease;
}

.bulk-actions-dropdown button:hover {
  background: #f1f5f9;
}

.bulk-actions-dropdown button.delete {
  color: #dc2626;
}

.bulk-actions-dropdown button.delete:hover {
  background: #fee2e2;
}

.view-toggle-buttons {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
}

.view-toggle {
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #f1f5f9;
  color: #475569;
}

.view-toggle.active {
  background: #2563eb;
  color: white;
}

.view-icon {
  width: 18px;
  height: 18px;
  margin-right: 0.5rem;
}

.create-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 1rem;
}

.create-button:hover {
  background: #1d4ed8;
  transform: translateY(-1px);
}

.create-icon {
  width: 18px;
  height: 18px;
}

.refresh-button {
  padding: 0.5rem;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.refresh-button:hover {
  background: #f1f5f9;
}

.refresh-icon {
  width: 20px;
  height: 20px;
  color: #64748b;
}

.geo-button {
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #f1f5f9;
  color: #475569;
}

.geo-button.active {
  background: #2563eb;
  color: white;
}

.geo-icon {
  width: 18px;
  height: 18px;
  margin-right: 0.5rem;
}

@media (max-width: 1024px) {
  .lessons-header {
    margin-bottom: 1.5rem;
    padding: 1rem;
  }

  .lessons-stats {
    flex-direction: column;
    gap: 1rem;
  }

  .header-right {
    flex-direction: column;
    align-items: stretch;
  }

  .controls-group {
    flex-wrap: wrap;
  }

  .bulk-actions {
    width: 100%;
  }

  .bulk-actions-dropdown {
    width: 100%;
  }
}
</style> 